import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useNotifications } from "@/features/notifications/NotificationsProvider"
import { USER_MENU_SETTINGS_ITEMS } from "@/widgets/layout/userMenuSettingsItems"

type NavbarUserMenuItemsProps = {
  layout: "dropdown" | "mobile"
  onNavigate?: () => void
  onLogout: () => void
}

const isSettingsItemActive = (pathname: string, search: string, to: string) => {
  const [path, query = ""] = to.split("?")
  if (pathname !== path) return false
  if (!query) return search === "" || search === "?"
  const params = new URLSearchParams(query)
  const current = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
  for (const [key, value] of params.entries()) {
    if (current.get(key) !== value) return false
  }
  return true
}

const isProfileActive = (pathname: string, search: string) =>
  pathname === "/profile" && !search.includes("section=notifications")

const menuItemClass = (active: boolean, layout: NavbarUserMenuItemsProps["layout"]) => {
  const padding = layout === "dropdown" ? "px-4 py-3" : "py-2"
  return [
    "w-full text-left text-sm transition-colors flex items-center gap-2",
    padding,
    active
      ? "text-lib-accent bg-lib-bg"
      : "text-lib-text-secondary hover:text-lib-accent hover:bg-lib-bg",
  ].join(" ")
}

export const NavbarUserMenuItems = ({ layout, onNavigate, onLogout }: NavbarUserMenuItemsProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { unreadCount } = useNotifications()
  const unreadDisplay = unreadCount > 99 ? "99+" : String(unreadCount)

  const handleNavigate = () => {
    onNavigate?.()
  }

  const handleLogout = () => {
    onLogout()
    handleNavigate()
  }

  const profileActive = isProfileActive(location.pathname, location.search)

  return (
    <>
      <Link
        to="/profile"
        onClick={handleNavigate}
        className={menuItemClass(profileActive, layout)}
        aria-current={profileActive ? "page" : undefined}
      >
        <i className="ri-user-line shrink-0" aria-hidden />
        <span className="min-w-0">{t("nav.profile")}</span>
      </Link>

      {USER_MENU_SETTINGS_ITEMS.length > 0 ? (
        <div className="border-t border-lib-border">
          <p className="px-4 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-widest text-lib-text-muted">
            {t("nav.settings")}
          </p>
          {USER_MENU_SETTINGS_ITEMS.map((item) => {
            const active = isSettingsItemActive(location.pathname, location.search, item.to)
            const showBadge = item.badge === "notifications-unread" && unreadCount > 0
            return (
              <Link
                key={item.key}
                to={item.to}
                onClick={handleNavigate}
                className={menuItemClass(active, layout)}
                aria-current={active ? "page" : undefined}
                aria-label={
                  showBadge ? t("notifications.sidebar.itemUnreadAria", { count: unreadCount }) : undefined
                }
              >
                <i className={`${item.icon} shrink-0`} aria-hidden />
                <span className="min-w-0 flex-1">{t(item.labelKey)}</span>
                {showBadge ? (
                  <span
                    aria-hidden
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-lib-accent text-white text-[11px] font-bold leading-none flex items-center justify-center"
                  >
                    {unreadDisplay}
                  </span>
                ) : null}
              </Link>
            )
          })}
        </div>
      ) : null}

      <div className="border-t border-lib-border">
        <button type="button" onClick={handleLogout} className={menuItemClass(false, layout)}>
          <i className="ri-logout-box-r-line shrink-0" aria-hidden />
          <span className="min-w-0">{t("nav.logout")}</span>
        </button>
      </div>
    </>
  )
}
